/**
 * StepShell — common chrome around every onboarding step:
 *
 *   ┌────────────────────────────────────────────────┐
 *   │  Logo                            ✕ Skip tour  │  <- header
 *   │  ──━━━━●○○○○                                   │  <- progress dots
 *   │   1 of 6 · Welcome                             │
 *   ├────────────────────────────────────────────────┤
 *   │                                                │
 *   │             {step children}                    │
 *   │                                                │
 *   ├────────────────────────────────────────────────┤
 *   │  Back                              Continue →  │
 *   └────────────────────────────────────────────────┘
 *
 * A step renders its body inside <StepShell>; the shell handles all the
 * navigation, progress, and chrome so each step file stays focused on
 * content. The "Continue" button can be disabled by the step (e.g. the
 * profile step disables it until a role is picked).
 */
import React from 'react';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import BrandLogo from '../../../components/brand/BrandLogo';
import { STEPS, STEP_LABELS, StepId } from '../types';

interface Props {
  step: StepId;
  onBack?: () => void;
  onNext?: () => void;
  onSkip?: () => void;
  /** Hide the Back/Next bar — used for the final celebratory step. */
  hideNav?: boolean;
  /** Override the next button label. */
  nextLabel?: string;
  /** Disable the Continue button (e.g. profile step needs a role first). */
  nextDisabled?: boolean;
  /** Title shown under the progress strip — overrides the default step label. */
  title?: string;
  /** One-line subtitle shown below the title. */
  subtitle?: string;
  children: React.ReactNode;
}

export default function StepShell({
  step,
  onBack,
  onNext,
  onSkip,
  hideNav,
  nextLabel = 'Continue',
  nextDisabled,
  title,
  subtitle,
  children,
}: Props) {
  const idx = STEPS.indexOf(step);
  const total = STEPS.length;

  return (
    <div
      className="min-h-screen w-full text-slate-100"
      style={{
        background:
          'radial-gradient(1200px 600px at 50% -20%, rgba(6,182,212,0.15), transparent 60%),' +
          'radial-gradient(800px 500px at 90% 110%, rgba(59,130,246,0.12), transparent 60%),' +
          '#0B1220',
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="px-6 md:px-10 py-5 flex items-center justify-between">
        <BrandLogo size="md" tagline />
        {onSkip && (
          <button
            onClick={onSkip}
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"
            title="Skip tour — you can revisit it from Help"
          >
            <X className="w-3.5 h-3.5" />
            <span>Skip tour</span>
          </button>
        )}
      </header>

      {/* ── Progress dots ──────────────────────────────────────────────── */}
      <div className="px-6 md:px-10">
        <div className="flex items-center gap-1.5">
          {STEPS.map((s, i) => {
            const done   = i <  idx;
            const active = i === idx;
            return (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                  active ? 'bg-cyan-400'
                : done   ? 'bg-cyan-700'
                         : 'bg-slate-800'
                }`}
                style={active ? { boxShadow: '0 0 12px rgba(6,182,212,0.6)' } : undefined}
              />
            );
          })}
        </div>
        <div className="mt-2 flex items-baseline gap-2 text-xs">
          <span className="text-slate-500 uppercase tracking-wider font-semibold">
            Step {idx + 1} of {total}
          </span>
          <span className="text-slate-400">·</span>
          <span className="text-cyan-300 font-semibold">{STEP_LABELS[step]}</span>
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <main className="px-6 md:px-10 py-8 md:py-10 max-w-5xl mx-auto">
        {(title || subtitle) && (
          <div className="mb-7 md:mb-9">
            {title && (
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="mt-2 text-slate-400 max-w-2xl text-base md:text-lg leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>
        )}

        <div
          key={step}
          className="animate-[fadeInUp_0.45s_cubic-bezier(0.2,0.8,0.2,1)]"
          style={{
            // Defined inline so CRA's CSS purge doesn't strip a one-off keyframe
            ...({} as React.CSSProperties),
          }}
        >
          {children}
        </div>
      </main>

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      {!hideNav && (
        <footer className="px-6 md:px-10 pb-10 max-w-5xl mx-auto flex items-center justify-between gap-3">
          {onBack ? (
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800/60 transition-all duration-150 text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
          ) : (
            <span />
          )}
          {onNext && (
            <button
              onClick={onNext}
              disabled={nextDisabled}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-sm font-semibold shadow-md shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-cyan-500/30"
            >
              <span>{nextLabel}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </footer>
      )}

      {/* Inline keyframes so we don't depend on the project's tailwind.config */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
